import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import { extractErrorMessage } from '../lib/errors';

interface User {
  id: string;
  email: string;
  name: string;
  role?: string;
  avatar_url?: string | null;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<any>;
  register: (email: string, password: string, name: string) => Promise<any>;
  logout: () => void;
  // v1.8 · allow subpages to update user info after PATCH /auth/me
  updateUser: (patch: Partial<User> | User) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount
    const token = localStorage.getItem('nexus_token');
    if (token) {
      apiService
        .getMe()
        .then((userData) => {
          setUser(userData);
        })
        .catch(() => {
          localStorage.removeItem('nexus_token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await apiService.login(email, password);
      localStorage.setItem('nexus_token', response.access_token);
      setUser(response.user);
      toast.success('¡Bienvenido a Nexus!');
      return response;
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Error al iniciar sesión'));
      throw error;
    }
  };

  const register = async (email: string, password: string, name: string) => {
    try {
      const response = await apiService.register(email, password, name);
      localStorage.setItem('nexus_token', response.access_token);
      setUser(response.user);
      toast.success('¡Cuenta creada exitosamente!');
      return response;
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Error al crear cuenta'));
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('nexus_token');
    setUser(null);
    toast.info('Sesión cerrada');
  };

  const updateUser = (patch: Partial<User> | User) => {
    setUser((prev) => (prev ? { ...prev, ...patch } as User : (patch as User)));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        updateUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
